import React, { useState, useEffect, useMemo } from "react";
import {
  Popover,
  Box,
  Typography,
  Checkbox,
  Button,
  Divider,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import CircularProgress from "@mui/material/CircularProgress";
import { size } from "lodash";

const NAVY = "#0D3B66";
const SELECTED_BG = "#EEF3FF";
const SELECTED_BORDER = "#B9CBFB";

const SelectFilesMenu = ({
  anchorEl,
  open,
  onClose,
  files = [],
  selectedIds = [],
  onApply,
  loading = false,
  searchable = true,
  onLoadMore,
  hasMore,
  searchValue = "",
  onSearchChange,
}) => {
  const [localSelected, setLocalSelected] = useState(selectedIds);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (open) {
      setLocalSelected(selectedIds);
    }
  }, [open, selectedIds]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    if (scrollTop + clientHeight >= scrollHeight - 10 && hasMore && !loading) {
      onLoadMore?.();
    }
  };

  const visibleFiles = files;

  const toggleOne = (id) =>
    setLocalSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const selectAll = () => setLocalSelected(visibleFiles.map((f) => f.id));
  const clearAll = () => setLocalSelected([]);

  const handleCancel = () => {
    setLocalSelected(selectedIds);
    onClose?.();
  };

  const handleApply = () => {
    onApply?.(localSelected);
    onClose?.();
  };

  const fileLabel =
    localSelected.length === 1 ? "File Selected" : "Files Selected";

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={handleCancel}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      PaperProps={{
        sx: {
          width: 300,
          borderRadius: 2,
          mt: 0.5,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header band with gray background, matching your table's group-header strip */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1.25,
          bgcolor: "#F1F5F9",
          borderBottom: "1px solid #E2E8F0",
        }}
      >
        <Button
          size="small"
          onClick={selectAll}
          sx={{
            textTransform: "none",
            fontWeight: 400,
            fontSize: "12px",
            color: "#475569",
            p: 0,
            minWidth: 0,
          }}
        >
          Select All
        </Button>
        <Button
          size="small"
          onClick={clearAll}
          sx={{
            textTransform: "none",
            fontWeight: 400,
            fontSize: "12px",
            color: "#475569",
            p: 0,
            minWidth: 0,
          }}
        >
          Clear All
        </Button>
      </Box>

      {searchable && (
        <Box sx={{ px: 1.5, pt: 1.25 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search files"
            value={searchValue}
            onChange={(e) => onSearchChange?.(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#94A3B8" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "12px",
                bgcolor: "#fff",
              },
            }}
          />
        </Box>
      )}

      <Box
        onScroll={handleScroll}
        sx={{
          maxHeight: 300,
          overflowY: "auto",
          px: 1.5,
          pt: 1.25,
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#CBD5E1", borderRadius: 3 },
        }}
      >
        {visibleFiles.length === 0 && !loading ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: 1, py: 2, textAlign: "center" }}
          >
            No files found
          </Typography>
        ) : (
          <>
            {visibleFiles.map((file) => {
              const checked = localSelected.includes(file.id);

              return (
                <Box
                  key={file.id}
                  onClick={() => toggleOne(file.id)}
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "flex-start",
                    border: `1.5px solid ${
                      checked ? SELECTED_BORDER : "#E2E8F0"
                    }`,
                    borderRadius: 1.5,
                    p: 1.1,
                    cursor: "pointer",
                    bgcolor: checked ? SELECTED_BG : "#fff",
                    transition:
                      "background-color 0.15s ease, border-color 0.15s ease",
                    "&:hover": {
                      bgcolor: checked ? SELECTED_BG : "#F8FAFC",
                    },
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={checked}
                    onChange={() => toggleOne(file.id)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      p: 0.25,
                      mt: 0.15,
                      color: "#CBD5E1",
                      "&.Mui-checked": { color: NAVY },
                      "& .MuiSvgIcon-root": { fontSize: 21 },
                    }}
                  />

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#334155",
                        lineHeight: 1.3,
                      }}
                    >
                      {file.dateRange}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "10px",
                        fontWeight: 500,
                        color: "#94A3B8",
                        mt: 0.25,
                      }}
                    >
                      File: {file.fileName}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "10px",
                        fontWeight: 400,
                        color: "#94A3B8",
                        mt: 0.25,
                        wordBreak: "break-all",
                        lineHeight: 1.4,
                      }}
                    >
                      Load ID: {file.loadId}
                    </Typography>
                  </Box>
                </Box>
              );
            })}

            {loading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 2,
                }}
              >
                {/* <Typography variant="body2" color="text.secondary">
          Loading more files...
        </Typography> */}
                <CircularProgress size={30} />
              </Box>
            )}
          </>
        )}
      </Box>

      <Divider sx={{ mt: 1.5, borderColor: "#E2E8F0" }} />

      {/* Footer — two rows: count on top, buttons below */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
        }}
      >
        <Typography
          sx={{ fontSize: "11px", fontWeight: 400, color: "#64748B" }}
        >
          {localSelected.length} {fileLabel}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button
            onClick={handleCancel}
            sx={{
              textTransform: "none",
              fontWeight: 400,
              fontSize: "12px",
              color: "#475569",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{
              textTransform: "none",
              fontWeight: 400,
              fontSize: "12px",
              bgcolor: "#0D3B66",
              boxShadow: "none",
              borderRadius: 1.5,
              px: 2.5,
            }}
          >
            Apply Selection
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default SelectFilesMenu;
